import Phaser from 'phaser';

import {
	createInitialItems,
	createLaneGeometry,
	createRng,
	normalizeCountPerSide,
	wrapAndAdvanceItems,
} from '@/features/laneChooserPhaser/game/utils/sideElements';
import { getLaneBoundaryPoseAtY } from '@/features/laneChooserPhaser/game/utils/laneShape';
import {
	computeInitialLiftYFromWidth,
	getInboardOffsetPx,
	getInboardOffsetScale,
	getSideElementSizePx,
} from '@/features/laneChooserPhaser/game/utils/responsive';

import type { LaneChooserSideElementAsset, LaneChooserSideElements } from '@/features/laneChooserPhaser/game/types';
import type { LaneShapeMetrics } from '@/features/laneChooserPhaser/game/utils/laneShape';
import type {
	LaneGeometry,
	SideElementItem,
	SideElementsRng,
} from '@/features/laneChooserPhaser/game/utils/sideElements';

const DEFAULT_SIDE_ELEMENTS_TRAVEL_SECONDS = 28;
const DEFAULT_SIDE_ELEMENT_SCALE_AT_TOP = 0.5;
const DEFAULT_SIDE_ELEMENT_SCALE_AT_BOTTOM = 1;
const SIDE_ELEMENTS_RNG_SEED = 0x5eed;

type SideElementRuntime = {
	// Mutable lane-side state that drives sprite position, texture swaps, and wrap-around.
	item: SideElementItem;
	// Phaser image displayed for the current side element item.
	sprite: Phaser.GameObjects.Image;
};

type SideElementsControllerConfig = {
	// Scene used to create and destroy Phaser display objects.
	scene: Phaser.Scene;
	// Parent container that keeps bank props inside the theme world display list.
	container: Phaser.GameObjects.Container;
	// Shared lane bezier metrics used to keep props aligned to the actual lane borders.
	laneMetrics: LaneShapeMetrics;
	// Horizontal conversion from viewport units into scene units.
	renderScaleX: number;
	// Vertical conversion from viewport units into scene units.
	renderScaleY: number;
	// Viewport width used by the legacy helpers to pick responsive lane percentages.
	viewportWidth: number;
	// Theme-specific motion and sizing configuration for side props.
	themeConfig: LaneChooserSideElements;
};

export class SideElementsController {
	// Scene reference used for sprite creation and texture lookups.
	private readonly scene: Phaser.Scene;
	// Container that owns all bank prop sprites.
	private readonly container: Phaser.GameObjects.Container;
	// Per-texture config used to resolve inset values for the current prop sprite.
	private readonly assetConfigByTextureKey: Map<string, LaneChooserSideElementAsset>;
	// Base sprite size before depth scaling is applied.
	private readonly elementSize: number;
	// Horizontal conversion from viewport units into scene units.
	private readonly renderScaleX: number;
	private readonly defaultEdgeInsetPx: number;
	// Responsive scale that keeps custom edge insets visually aligned across viewport sizes.
	private readonly edgeInsetScale: number;
	// Legacy lane-side geometry used for bank sampling and wrap ranges.
	private readonly geometry: LaneGeometry;
	// Travel direction for the whole prop stream.
	private motionDirection: 1 | -1;
	// Additional speed multiplier from theme config.
	private motionScale: number;
	// Deterministic RNG reused for initial placement and wrapped texture swaps.
	private readonly rng: SideElementsRng;
	// Largest depth scale used near the bottom of the lane.
	private readonly scaleAtBottom: number;
	// Smallest depth scale used near the top of the lane.
	private readonly scaleAtTop: number;
	// Runtime list of visible sprites and their movement state.
	private readonly sprites: SideElementRuntime[];
	// Texture pool available for sprite assignment and wrap-time variation.
	private readonly textureKeys: string[];
	// Base speed that moves props through the loop before the motion multiplier is applied.
	private readonly baseSpeedPxPerSec: number;
	// Shared lane metrics used to sample border points and inward normals.
	private readonly laneMetrics: LaneShapeMetrics;
	// Prevents updates from running after the controller has been torn down.
	private destroyed = false;

	// Creates the side-element stream and seeds the initial sprites on both lane borders.
	constructor({
		scene,
		container,
		laneMetrics,
		renderScaleX,
		renderScaleY,
		viewportWidth,
		themeConfig,
	}: SideElementsControllerConfig) {
		this.scene = scene;
		this.container = container;
		this.laneMetrics = laneMetrics;
		this.renderScaleX = renderScaleX;
		this.edgeInsetScale = getInboardOffsetScale(viewportWidth);
		this.defaultEdgeInsetPx = getInboardOffsetPx(viewportWidth) * renderScaleX;
		this.elementSize = getSideElementSizePx(viewportWidth) * renderScaleX;
		this.assetConfigByTextureKey = new Map(themeConfig.assets.map((asset) => [asset.textureKey, asset]));
		this.motionDirection = themeConfig.motionDirection === -1 ? -1 : 1;
		this.motionScale = Math.max(0, themeConfig.motionScale ?? 1);
		this.rng = createRng(SIDE_ELEMENTS_RNG_SEED);
		this.scaleAtTop = themeConfig.scaleAtTop ?? DEFAULT_SIDE_ELEMENT_SCALE_AT_TOP;
		this.scaleAtBottom = themeConfig.scaleAtBottom ?? DEFAULT_SIDE_ELEMENT_SCALE_AT_BOTTOM;
		this.textureKeys = themeConfig.assets
			.map((asset) => asset.textureKey)
			.filter((textureKey) => this.scene.textures.exists(textureKey));

		this.geometry = createLaneGeometry({
			height: this.laneMetrics.height,
			elementSize: this.elementSize,
		});

		const initialItems = createInitialItems({
			sides: 'both',
			geometry: this.geometry,
			itemsPerSide: normalizeCountPerSide(themeConfig.countPerSide),
			elementSize: this.elementSize,
			rng: this.rng,
			sprites: this.textureKeys,
		});
		const travelDurationSeconds = Math.max(
			1,
			themeConfig.travelDurationSeconds ?? DEFAULT_SIDE_ELEMENTS_TRAVEL_SECONDS,
		);
		this.baseSpeedPxPerSec = Math.max(
			1,
			(this.laneMetrics.height + Math.abs(computeInitialLiftYFromWidth(viewportWidth)) * renderScaleY) /
				travelDurationSeconds,
		);
		this.sprites = initialItems.map((item) => this.createSprite(item));
	}

	// Advances all props through the loop and reapplies border-following positions every frame.
	update(delta: number) {
		if (this.destroyed || delta <= 0 || !this.sprites.length) {
			return;
		}

		const nextItems = wrapAndAdvanceItems({
			items: this.sprites.map((sideElement) => sideElement.item),
			motionScale: this.motionScale,
			motionDirection: this.motionDirection,
			baseSpeedPxPerSec: this.baseSpeedPxPerSec,
			deltaTime: delta / 1000,
			rng: this.rng,
			loopMin: this.geometry.loopMin,
			loopMax: this.geometry.loopMax,
			loopSpan: this.geometry.loopSpan,
			sprites: this.textureKeys,
		});

		nextItems.forEach((item, index) => {
			const sideElement = this.sprites[index];
			if (!sideElement?.sprite.active) {
				return;
			}

			if (sideElement.item.image !== item.image) {
				sideElement.sprite.setTexture(item.image);
			}

			sideElement.item = item;
			this.syncSprite(sideElement);
		});
	}

	// Updates the shared loop speed and direction used by all side props.
	setMotion(motionScale: number, motionDirection: 1 | -1) {
		this.motionScale = Math.max(0, motionScale);
		this.motionDirection = motionDirection === -1 ? -1 : 1;
	}

	// Destroys the controller-owned sprites when the scene rerenders or shuts down.
	destroy() {
		if (this.destroyed) {
			return;
		}

		this.destroyed = true;
		this.sprites.forEach(({ sprite }) => {
			if (sprite.active) {
				sprite.destroy();
			}
		});
	}

	// Resolves one asset-specific inset value and converts it into scene units.
	private getScaledEdgeInsetPx(assetConfig: LaneChooserSideElementAsset | undefined): number {
		if (assetConfig?.edgeInsetPx == null) {
			return this.defaultEdgeInsetPx;
		}

		return assetConfig.edgeInsetPx * this.edgeInsetScale * this.renderScaleX;
	}

	// Creates one Phaser sprite for an initial item and places it on the correct lane border.
	private createSprite(item: SideElementItem): SideElementRuntime {
		const sprite = this.scene.add.image(0, 0, item.image);
		sprite.setOrigin(0.5, 0.5);

		const runtime = { item, sprite };
		this.syncSprite(runtime);
		this.container.add(sprite);

		return runtime;
	}

	// Samples the actual rendered lane borders, applies the interpolated inset, and scales one sprite by depth.
	private syncSprite(sideElement: SideElementRuntime) {
		const { item, sprite } = sideElement;
		const leftBoundaryPose = getLaneBoundaryPoseAtY(this.laneMetrics, item.y, 'left');
		const rightBoundaryPose = getLaneBoundaryPoseAtY(this.laneMetrics, item.y, 'right');
		const leftBankX = leftBoundaryPose.point.x;
		const rightBankX = rightBoundaryPose.point.x;
		const normalizedDepth = Phaser.Math.Clamp(item.y / this.laneMetrics.height, 0, 1);
		const assetConfig = this.assetConfigByTextureKey.get(item.image);
		const currentEdgeInset = this.getScaledEdgeInsetPx(assetConfig);
		const displayScale = this.scaleAtTop + (this.scaleAtBottom - this.scaleAtTop) * normalizedDepth;
		const towardCenter = item.xSide === 'left' ? 1 : -1;
		const maxDisplaySize = this.elementSize * displayScale * Math.max(0, assetConfig?.sizeScale ?? 1);
		const assetWidth = Math.max(1, assetConfig?.assetSize.width ?? 1);
		const assetHeight = Math.max(1, assetConfig?.assetSize.height ?? 1);
		const displayWidth = assetWidth >= assetHeight ? maxDisplaySize : maxDisplaySize * (assetWidth / assetHeight);
		const displayHeight = assetHeight >= assetWidth ? maxDisplaySize : maxDisplaySize * (assetHeight / assetWidth);
		const halfDisplayWidth = displayWidth / 2;
		let x =
			(item.xSide === 'left' ? leftBankX : rightBankX) +
			towardCenter * (halfDisplayWidth + currentEdgeInset) +
			item.jitter;
		const outwardOverlapPx = Math.min(currentEdgeInset, 0);
		const minX = leftBankX + halfDisplayWidth + outwardOverlapPx;
		const maxX = rightBankX - halfDisplayWidth - outwardOverlapPx;

		x = Math.max(minX, Math.min(maxX, x));

		sprite.setPosition(x, this.laneMetrics.topOffset + item.y);
		sprite.setDisplaySize(displayWidth, displayHeight);
	}
}
